import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { client } from '../api/client'
import { NavigationBar } from '../components/NavigationBar'
import { useAuthStore } from '../stores/auth'

export function VerificationPendingPage() {
  const { t } = useTranslation('auth')
  const parent = useAuthStore((state) => state.parent)
  const [isResending, setIsResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const navigate = useNavigate()

  // Redirect if user is authenticated and email is verified
  useEffect(() => {
    if (parent && parent.is_email_verified) {
      navigate('/')
    }
  }, [parent, navigate])

  const handleResendVerification = async () => {
    try {
      setIsResending(true)
      setResendMessage(null)
      const response = await client.post('/auth/resend-verification/')
      setResendMessage({
        type: 'success',
        text: t('verification.pending.resendSuccess')
      })
    } catch (error: any) {
      console.error('Resend verification error:', error.response?.data);
      
      // Check for rate limiting response (429 Too Many Requests)
      if (error.response?.status === 429) {
        if (error.response.data?.rate_limited && error.response.data?.minutes_remaining) {
          // Use structured rate limit response
          setResendMessage({
            type: 'error',
            text: t('verification.pending.rateLimitedWithTime', { 
              minutes: error.response.data.minutes_remaining 
            })
          });
        } else {
          // Fallback to generic rate limit message
          setResendMessage({
            type: 'error',
            text: t('verification.pending.rateLimited')
          });
        }
      } else {
        // Other resend verification error handling
        setResendMessage({
          type: 'error',
          text: error.response?.data?.message || t('verification.pending.resendError')
        })
      }
    } finally {
      setIsResending(false)
    }
  }

  return (
    <>
      <NavigationBar />

      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <div className="max-w-md w-full">
          <div className="card-neobrutalism">
            <h2 className="text-3xl font-black text-neobrutalism-text text-center mb-6">
              {t('verification.title')}
            </h2>
            <div className="space-y-6">
              {/* Main verification message - prettier styling without black border */}
              <div className="bg-neobrutalism-yellow/50 p-6 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-black" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-lg font-bold">
                      {t('verification.pending.message', { 
                        email: <span className="underline">{parent?.email}</span>,
                        interpolation: { escapeValue: false }
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* New section for spam folder message */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-md text-blue-800">
                      {t('verification.pending.spamMessage')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <p className="text-lg font-bold mb-2">{t('verification.pending.resend')}</p>
                {resendMessage && (
                  <div className={`mb-4 p-3 rounded-lg border-2 border-black ${
                    resendMessage.type === 'success' ? 'bg-neobrutalism-green/20' : 'bg-neobrutalism-red/20'
                  }`}>
                    <p className="font-bold">{resendMessage.text}</p>
                  </div>
                )}
                <button 
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className={`btn-neobrutalism bg-neobrutalism-blue text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4 ${isResending ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isResending ? t('verification.pending.resending') : t('verification.pending.resendButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
} 